/**
 * <s-product-gallery> — Shows one product photo at a time. For products with colours the
 * page shows one photo per colour (each colour's first photo as a thumbnail); the other views
 * of a colour are browsed with arrows inside the enlarged view (see s-product-image-zoom).
 *
 * Required markup:
 *   [data-gallery-slide][data-media-id][data-colour]
 *                                         — One per media item; all but the visible one carry `hidden`.
 *
 * Optional markup:
 *   [data-colour] on the host              — Currently selected colour handle.
 *   button[data-gallery-thumb][data-media-id][commandfor][command="--show-media"]
 *                                         — Thumbnails; aria-current marks the visible photo.
 *                                           With [data-colour-thumb], clicking also selects that colour.
 *   script[type="application/json"][data-variant-media]
 *                                         — { "<variant id>": "<media id>" } map.
 *   script[type="application/json"][data-variant-colour]
 *                                         — { "<variant id>": "<colour handle>" } map.
 *
 * External coupling:
 *   Listens for bubbling `change` events from variant inputs carrying data-variant-id
 *   (or select options carrying it) inside the closest [data-product-detail] ancestor.
 */
const handleize = (value) => String(value).toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

class SProductGallery extends HTMLElement {
  #ac;
  #variantMedia = {};
  #variantColour = {};

  #readJson(selector) {
    const el = this.querySelector(selector);
    if (!el?.textContent) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('[s-product-gallery] Failed to parse', selector, error);
      return {};
    }
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#variantMedia = this.#readJson('script[type="application/json"][data-variant-media]');
    this.#variantColour = this.#readJson('script[type="application/json"][data-variant-colour]');

    this.addEventListener('command', (event) => {
      if (event.command !== '--show-media') return;
      const source = event.source;
      const mediaId = source?.dataset?.mediaId;
      if (!mediaId) return;
      this.show(mediaId);
      if (source.hasAttribute('data-colour-thumb')) {
        this.dataset.colour = source.dataset.colour || '';
        this.#selectColour(source.dataset.colour);
      }
    }, opts);

    const root = this.closest('[data-product-detail]');
    root?.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement)) return;
      const variantId = input.dataset.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
      if (!variantId) return;
      // Only a colour change moves the photo; picking a size keeps the photo the shopper is looking at.
      const hasColours = Object.keys(this.#variantColour).length > 0;
      if (hasColours) {
        const colour = this.#variantColour[variantId];
        if (colour === undefined || colour === (this.dataset.colour || '')) return;
        this.dataset.colour = colour;
        const cover = this.querySelector(`[data-colour-thumb][data-colour="${CSS.escape(colour)}"]`);
        if (cover) return this.show(cover.dataset.mediaId);
      }
      const mediaId = this.#variantMedia[variantId];
      if (mediaId) this.show(String(mediaId));
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /** Ticks the matching colour swatch so price, stock and cart follow the photo. */
  #selectColour(colour) {
    if (!colour) return;
    const root = this.closest('[data-product-detail]');
    const inputs = root?.querySelectorAll('input[type="radio"][data-variant-id]') || [];
    for (const input of inputs) {
      if (handleize(input.value) !== colour) continue;
      if (this.#variantColour[input.dataset.variantId] !== colour) continue;
      if (!input.checked && !input.disabled) input.click();
      return;
    }
    const select = [...(root?.querySelectorAll('select') || [])].find((el) =>
      [...el.options].some((option) => handleize(option.value) === colour && this.#variantColour[option.dataset.variantId] === colour));
    if (select) {
      const option = [...select.options].find((o) => handleize(o.value) === colour);
      if (option && !option.selected) {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  show(mediaId) {
    const slides = this.querySelectorAll('[data-gallery-slide]');
    const target = [...slides].find((slide) => slide.dataset.mediaId === mediaId);
    if (!target) return;

    for (const slide of slides) {
      slide.hidden = slide !== target;
    }
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      if (thumb.dataset.mediaId === mediaId) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
